import { useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";

import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useLanguage } from "./LanguageContext";
import { InvestmentEvolutionChart } from "./InvestmentEvolutionChart";
import { supabase, db } from "@/lib/supabase";
import { TablePagination } from "./ui/table-pagination";
import { useEffect } from "react";

// Clean icon components
const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrendingUpIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22,7 13.5,15.5 8.5,10.5 2,17"></polyline>
    <polyline points="16,7 22,7 22,13"></polyline>
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const LockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <circle cx="12" cy="16" r="1"></circle>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 12 2 2 4-4"></path>
    <circle cx="12" cy="12" r="10"></circle>
  </svg>
);

const DocumentIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
  </svg>
);

const DocumentOffIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 3l18 18"></path>
    <path d="M14 2H6a2 2 0 0 0-2 2v12"></path>
    <path d="M8 21h8"></path>
    <path d="M20 8v6a2 2 0 0 1-2 2H9"></path>
  </svg>
);

export function Aportaciones() {
  // The request-authorization modal is disabled for the initial app
  // version. We keep the state variable for future re-enabling, but
  // all setter calls are replaced with no-ops and the Dialog is
  // forced closed so the UI cannot open it.
  const [solicitudAportacionOpen, setSolicitudAportacionOpen] = useState(false);
  const [confirmacionOpen, setConfirmacionOpen] = useState(false);
  const { t, getBilingualText } = useLanguage();

  const [investorId, setInvestorId] = useState<string | null>(null);
  const [loadingInvestorId, setLoadingInvestorId] = useState(false);
  const [investorError, setInvestorError] = useState<string | null>(null);
  const [loadingContributions, setLoadingContributions] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  

  useEffect(() => {
    let mounted = true;
    async function resolveInvestor() {
      try {
        setLoadingInvestorId(true);
        const authResult = await supabase.auth.getUser();
        const authErr = (authResult as any)?.error;
        if (authErr) throw authErr;
        // Support both Supabase response shapes: { data: { user } } and { user }
        const maybeUser =
          (authResult as any)?.data?.user ?? (authResult as any)?.user;
        const userId = maybeUser?.id;
        if (!userId) throw new Error("No authenticated user");

        const profile = await db.getInvestorProfile(userId);
        if (!profile || !profile.id)
          throw new Error("Investor profile not found");
        if (mounted) setInvestorId(profile.id);
      } catch (err: any) {
        console.error(err);
        if (mounted) setInvestorError(err?.message || String(err));
      } finally {
        if (mounted) setLoadingInvestorId(false);
      }
    }

    resolveInvestor();
    return () => {
      mounted = false;
    };
  }, []);

  const [aportaciones, setAportaciones] = useState<
    {
      fecha: string;
      concepto: string;
      importe: string;
      porcentaje: number;
      documentUrl?: string | null;
    }[]
  >([]);

  const [solicitudData, setSolicitudData] = useState({
    cantidad: "",
    concepto: "",
  });

  const tableRef = useRef<HTMLTableElement | null>(null);
  const topScrollerRef = useRef<HTMLDivElement | null>(null);
  const topInnerRef = useRef<HTMLDivElement | null>(null);
  const bottomWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const formatCurrency = (amount: number) => {
    try {
      return `€${Math.round(amount).toLocaleString("es-ES")}`;
    } catch {
      return `€${Math.round(amount)}`;
    }
  };

  const formatDateReadable = (dateStr?: string) => {
    if (!dateStr) return "-";
    // Prefer parsing YYYY-MM-DD or full ISO and construct a local date to avoid timezone shifts
    const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    try {
      if (m) {
        const y = parseInt(m[1], 10);
        const mo = parseInt(m[2], 10) - 1;
        const d = parseInt(m[3], 10);
        return new Intl.DateTimeFormat("es-ES", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }).format(new Date(y, mo, d));
      }
      const dt = new Date(dateStr);
      if (!isNaN(dt.getTime())) {
        return new Intl.DateTimeFormat("es-ES", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }).format(dt);
      }
    } catch (e) {
      // fallback
    }
    return String(dateStr);
  };

  const formatPercentage = (p: number) => {
    try {
      return `${p.toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    } catch {
      return `${p.toFixed(2)}%`;
    }
  };

  // Server-side paginated fetch of contributions
  useEffect(() => {
    if (!investorId) return;
    let mounted = true;
    setLoadingContributions(true);

    // Use RPC get_contributions_by_investor and paginate client-side
    db.getContributionsByInvestor(investorId)
      .then((rows: any[]) => {
        if (!mounted) return;

        const all = rows || [];
        const totalCount = all.length;

        const from = Math.max(0, (page - 1) * pageSize);
        const to = from + pageSize;
        const pageRows = all.slice(from, to);

        const mapped = (pageRows || []).map((r: any) => {
          const dateStr = r.contribution_date || r.contributionDate || r.fecha || "";
          const fecha = dateStr ? formatDateReadable(dateStr) : "-";
          const concepto = r.note || r.description || "investment.concepts.additional";
          const importe = formatCurrency(Number(r.amount || 0));
          const porcentaje = Number(r.capital_percentage ?? r.capitalPercentage ?? 0);
          // try common field names that might contain a document link
          const documentUrl =
            r.document_url || r.documentUrl || r.file_url || r.fileUrl || r.link || r.document || null;

          return { fecha, concepto, importe, porcentaje, documentUrl };
        });

        setAportaciones(mapped);
        setTotal(Number(totalCount) || 0);
      })
      .catch((err: any) => {
        console.error("Error fetching contributions (RPC)", err);
        if (mounted) setInvestorError(err?.message || String(err));
      })
      .finally(() => {
        if (mounted) setLoadingContributions(false);
      });

    return () => {
      mounted = false;
    };
  }, [investorId, page, pageSize]);

  useEffect(() => {
    const syncWidths = () => {
      const table = tableRef.current;
      const topInner = topInnerRef.current;
      const bottom = bottomWrapperRef.current;
      if (!table || !topInner || !bottom) return;
      topInner.style.width = `${table.scrollWidth}px`;
      const isMobile = window.innerWidth < 768;
      setShowScrollHint(isMobile || bottom.scrollWidth > bottom.clientWidth);
    };

    syncWidths();
    window.addEventListener("resize", syncWidths);

    const top = topScrollerRef.current;
    const bottom = bottomWrapperRef.current;
    if (top && bottom) {
      const onTopScroll = () => { if (bottom) bottom.scrollLeft = top.scrollLeft; };
      const onBottomScroll = () => { if (top) top.scrollLeft = bottom.scrollLeft; };
      top.addEventListener("scroll", onTopScroll);
      bottom.addEventListener("scroll", onBottomScroll);
      return () => {
        top.removeEventListener("scroll", onTopScroll);
        bottom.removeEventListener("scroll", onBottomScroll);
        window.removeEventListener("resize", syncWidths);
      };
    }

    return () => window.removeEventListener("resize", syncWidths);
  }, [aportaciones.length, loadingContributions]);

  const handleSolicitudSubmit = () => {
    if (solicitudData.cantidad && solicitudData.concepto) {
      // Añadir nueva solicitud al historial
      const nuevaSolicitud = {
        fecha: formatDateReadable(new Date().toISOString()),
        concepto: solicitudData.concepto,
        importe: solicitudData.cantidad.includes("€")
          ? solicitudData.cantidad
          : `€${solicitudData.cantidad}`,
        porcentaje: 0,
        estado: "investment.status.pendingRequest",
      };

      setAportaciones((prev) => [nuevaSolicitud, ...prev]);
      setSolicitudAportacionOpen(false);
      setConfirmacionOpen(true);
      setSolicitudData({ cantidad: "", concepto: "" });
    }
  };

  return (
    <TooltipProvider>
      <div className="p-6 space-y-6">
        {/* Gráfico de Evolución Premium con métricas integradas */}
        {investorError && (
          <div className="mb-4 text-sm text-red-600">
            Error: {investorError}
          </div>
        )}
        {/* Always render the chart; while the parent is resolving the investor id
            the chart will show its own loading state. */}
        <InvestmentEvolutionChart investorId={investorId} isResolvingInvestor={loadingInvestorId} />

        <div className="space-y-6">
          {/* Historial de Aportaciones */}
          <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>
                  {getBilingualText("investment.history.title")}
                </CardTitle>
                <CardDescription>
                  {getBilingualText("investment.history.subtitle")}
                </CardDescription>
              </div>
              {/* pequeño indicador de carga */}
              <div className="ml-4">
                {loadingContributions && (
                  <div className="flex items-center text-sm text-gray-500">
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-gray-400"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    {getBilingualText("investment.table.loading") || "Cargando"}
                  </div>
                )}
              </div>
              {/* Botón con tooltip - Estructura corregida */}
              <UITooltip>
                <TooltipTrigger>
                  <Button
                    className="gap-2 bg-gray-400 hover:bg-gray-500 cursor-not-allowed"
                    onClick={() => {
                      // disabled in initial release
                    }}
                    disabled={true}
                    aria-busy={loadingInvestorId || loadingContributions}
                    title="Disabled in initial release"
                  >
                    <LockIcon />
                    {getBilingualText("investment.actions.contributeMore")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {getBilingualText(
                      "investment.tooltip.contributionsBlocked"
                    )}
                  </p>
                </TooltipContent>
              </UITooltip>

              

              {/* Modal de Solicitud de Aportación - Separado del tooltip */}
              <Dialog
                // disabled: never open in initial release
                open={false}
                onOpenChange={() => {
                  // intentionally no-op while feature is disabled
                }}
              >
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <LockIcon />
                      {getBilingualText("investment.modal.request.title")}
                    </DialogTitle>
                    <DialogDescription>
                      {getBilingualText("investment.modal.request.description")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="cantidad" className="text-right">
                        {getBilingualText(
                          "investment.modal.request.amountLabel"
                        )}
                      </Label>
                      <Input
                        id="cantidad"
                        placeholder={t(
                          "investment.modal.request.amountPlaceholder"
                        )}
                        className="col-span-3"
                        value={solicitudData.cantidad}
                        onChange={(e) =>
                          setSolicitudData((prev) => ({
                            ...prev,
                            cantidad: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="concepto" className="text-right">
                        {getBilingualText(
                          "investment.modal.request.conceptLabel"
                        )}
                      </Label>
                      <Input
                        id="concepto"
                        placeholder={t(
                          "investment.modal.request.conceptPlaceholder"
                        )}
                        className="col-span-3"
                        value={solicitudData.concepto}
                        onChange={(e) =>
                          setSolicitudData((prev) => ({
                            ...prev,
                            concepto: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        // disabled in initial release
                      }}
                    >
                      {getBilingualText("investment.actions.cancel")}
                    </Button>
                    <Button
                      className="bg-black hover:bg-gray-800 text-white"
                      onClick={handleSolicitudSubmit}
                      disabled={
                        !solicitudData.cantidad || !solicitudData.concepto
                      }
                    >
                      {getBilingualText(
                        "investment.actions.requestAuthorization"
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Modal de Confirmación */}
              <Dialog
                open={confirmacionOpen}
                onOpenChange={setConfirmacionOpen}
              >
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader className="text-center">
                    <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircleIcon />
                    </div>
                    <DialogTitle className="text-center">
                      {getBilingualText("investment.modal.success.title")}
                    </DialogTitle>
                    <DialogDescription className="text-center">
                      {getBilingualText("investment.modal.success.description")}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      className="w-full bg-black hover:bg-gray-800 text-white"
                      onClick={() => setConfirmacionOpen(false)}
                    >
                      {getBilingualText("investment.actions.close")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {showScrollHint && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2 md:hidden transition-opacity duration-300 opacity-100">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500" aria-hidden="true">
                    <path d="M3 12h18" />
                    <path d="M7 6L3 12L7 18" />
                    <path d="M17 6L21 12L17 18" />
                  </svg>
                  <span>Desliza horizontalmente</span>
                </div>
              )}
              <div ref={topScrollerRef} className="overflow-x-auto overflow-y-hidden" style={{ height: 16 }}>
                <div ref={topInnerRef} style={{ height: 1 }} />
              </div>
              <div ref={bottomWrapperRef} className="overflow-x-auto">
                <table ref={tableRef} className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">
                        {getBilingualText("investment.table.date")}
                      </th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">
                        {getBilingualText("investment.table.concept")}
                      </th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-gray-600">
                        Documento
                      </th>
                      <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">
                        {"Porcentaje"}
                      </th>
                      <th className="text-right py-3 px-2 text-sm font-medium text-gray-600">
                        {getBilingualText("investment.table.amount")}
                      </th>
                      {/* Status column intentionally removed */}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingContributions ? (
                      // skeleton rows
                      Array.from({ length: 6 }).map((_, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-3 px-2 text-sm">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
                          </td>
                          <td className="py-3 px-2 text-sm font-medium">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-48" />
                          </td>
                          <td className="py-3 px-2 text-sm text-center">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-6 mx-auto" />
                          </td>
                          <td className="py-3 px-2 text-sm text-right font-medium">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-20 ml-auto" />
                          </td>
                          <td className="py-3 px-2 text-sm text-right font-medium">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-24 ml-auto" />
                          </td>
                        </tr>
                      ))
                    ) : aportaciones.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-6 px-4 text-sm text-gray-600"
                        >
                          No hay aportaciones registradas.
                        </td>
                      </tr>
                    ) : (
                      aportaciones.map((aportacion, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-3 px-2 text-sm">
                            {aportacion.fecha}
                          </td>
                          <td className="py-3 px-2 text-sm font-medium">
                            {getBilingualText(aportacion.concepto)}
                          </td>
                          <td className="py-3 px-2 text-sm text-center">
                            <UITooltip>
                              <TooltipTrigger>
                                {aportacion.documentUrl ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const url = String(aportacion.documentUrl || "");
                                      let href = url;
                                      if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(href) && !href.startsWith("//")) {
                                        if (href.includes(".")) href = `https://${href}`;
                                      }
                                      window.open(href, "_blank", "noopener,noreferrer");
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        const url = String(aportacion.documentUrl || "");
                                        let href = url;
                                        if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(href) && !href.startsWith("//")) {
                                          if (href.includes(".")) href = `https://${href}`;
                                        }
                                        window.open(href, "_blank", "noopener,noreferrer");
                                      }
                                    }}
                                    className="inline-flex items-center justify-center p-1 text-blue-600 hover:text-blue-800"
                                    aria-label={t("investment.table.viewDocument")}
                                  >
                                    <DocumentIcon />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={true}
                                    aria-disabled={true}
                                    className="inline-flex items-center justify-center p-1 text-gray-300 cursor-not-allowed"
                                    title={
                                      typeof getBilingualText("investment.table.noDocument") === "string"
                                        ? (getBilingualText("investment.table.noDocument") as string)
                                        : undefined
                                    }
                                  >
                                    <DocumentIcon />
                                  </button>
                                )}
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {aportacion.documentUrl
                                    ? getBilingualText("investment.table.viewDocument")
                                    : getBilingualText("investment.table.noDocument")}
                                </p>
                              </TooltipContent>
                            </UITooltip>
                          </td>
                          <td className="py-3 px-2 text-sm text-right font-medium">
                            {formatPercentage(aportacion.porcentaje)}
                          </td>
                          <td className="py-3 px-2 text-sm text-right font-medium">
                            {aportacion.importe}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <TablePagination
              page={page}
              totalPages={Math.max(1, Math.ceil(total / pageSize))}
              pageSize={pageSize}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
